import { IProject } from '@/interfaces/project.interface';
import { IProjectTableData } from '@/components/projetos/project-table';
import { IProjectPerson } from '@/components/projetos/people-table';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Safely parse JSON string array to array
 * Returns empty array if parsing fails or input is null/undefined
 */
export function parseJsonStringArray(jsonString: string | null | undefined): string[] {
  if (!jsonString) return [];

  try {
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error parsing JSON string array:', error);
    return [];
  }
}

/**
 * Transforms API Movie response to table display format
 */
export function transformMovieToTableData(movie: IProject): IProjectTableData {
  // Proponent priority: person.name > juridic_person.social_name > "Não informado"
  const proponent = movie.producer?.name ||
                   movie.producer?.juridic_person?.social_name ||
                   'Não informado';

  return {
    id: movie.id,
    name: movie.name,
    salicNumber: movie.salic_number,
    proponent,
    status: mapMovieStatus(movie.status?.description || 'Rascunho'),
    releaseDate: movie.release_date
      ? formatDate(movie.release_date)
      : 'Não definida',
  };
}

/**
 * Maps backend status to frontend literal types
 * Per user requirement: Expand frontend to accept all backend status values
 */
export function mapMovieStatus(
  backendStatus: string
): "Em Desenvolvimento" | "Ativo" | "Concluído" | "Cancelado" {
  // Normalize status string
  const normalized = backendStatus.toLowerCase().trim();

  // Mapping table for common status variations
  const statusMap: Record<string, "Em Desenvolvimento" | "Ativo" | "Concluído" | "Cancelado"> = {
    // Development statuses
    'rascunho': 'Em Desenvolvimento',
    'em desenvolvimento': 'Em Desenvolvimento',
    'desenvolvimento': 'Em Desenvolvimento',
    'draft': 'Em Desenvolvimento',

    // Active statuses
    'ativo': 'Ativo',
    'em produção': 'Ativo',
    'produção': 'Ativo',
    'active': 'Ativo',
    'em andamento': 'Ativo',

    // Completed statuses
    'concluído': 'Concluído',
    'finalizado': 'Concluído',
    'completed': 'Concluído',
    'finished': 'Concluído',
    'entregue': 'Concluído',

    // Cancelled statuses
    'cancelado': 'Cancelado',
    'cancelled': 'Cancelado',
    'inativo': 'Cancelado',
    'suspenso': 'Cancelado',
  };

  return statusMap[normalized] || 'Em Desenvolvimento';
}

/**
 * Transform staff contracts to people table format
 * Per business rule: All project people MUST have contracts
 */
export function transformStaffContractsToPeople(
  staffContracts: IProject['staffContracts']
): IProjectPerson[] {
  if (!staffContracts || staffContracts.length === 0) {
    return [];
  }

  return staffContracts.map(contract => ({
    id: contract.id,
    name: contract.staffUser?.person?.name || 'Não informado',
    avatarUrl: "",
    phone: contract.staffUser?.person?.cellphone_number ||
           contract.staffUser?.person?.cellphone_number ||
           'Não informado',
    email: contract.staffUser?.login || 'Não informado',
    role: contract.staffUser?.person?.role || 'Função não definida',
    status: contract.active ? 'Ativo' : 'Inativo',
    registrationDate: formatDate(contract.createdAt),
  }));
}

/**
 * Aggregate all budgets to calculate total values
 * Per user requirement: Aggregate budget data, not just first item
 */
export function aggregateBudgetValues(budgets: IProject['budgets']) {
  if (!budgets || budgets.length === 0) {
    return {
      totalApproved: 0,
      totalCaptured: 0,
      totalRemaining: 0,
    };
  }

  const totalApproved = budgets.reduce((sum, budget) =>
    sum + parseFloat(budget.total_amount || '0'), 0);

  const totalCaptured = budgets.reduce((sum, budget) =>
    sum + parseFloat(budget.amount || '0'), 0);

  const totalRemaining = totalApproved - totalCaptured;

  return {
    totalApproved,
    totalCaptured,
    totalRemaining,
  };
}

/**
 * Format currency values to Brazilian Real
 */
export function formatCurrency(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numValue);
}

/**
 * Format date strings to Brazilian format (dd/MM/yyyy)
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return 'Não informada';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, 'dd/MM/yyyy', { locale: ptBR });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Data inválida';
  }
}

/**
 * Format movie duration from minutes to HH:MM:SS
 */
export function formatMovieDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '-';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

/**
 * Get CNPJ from producer (juridic person)
 */
export function getProducerCNPJ(producer: IProject['producer']): string {
  return producer?.juridic_person?.document || '';
}

/**
 * Get company name from producer
 */
export function getProducerCompanyName(producer: IProject['producer']): string {
  return producer?.juridic_person?.social_name ||
         producer?.name ||
         'Não informado';
}
